import { FC, useState, useCallback } from 'react';
import { Dialog, Box, Button, Typography, Slider, IconButton } from '@material-ui/core';
import { DialogCloseComponent } from 'Components/DialogCloseComponent';
import Cropper from 'react-easy-crop';
import { fontSizes, gutters } from 'Theme/Variables';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import { useLocalization } from 'Hooks';

import './ImageCropDialog.scss';

type ImageCropDialogProps = {
  open: boolean;
  hide: () => void;
  imageSrc: string;
  onSave: (croppedImage: string, imageType: string) => void;
  title?: string;
  saveButtonText?: string;
  cancelButtonText?: string;
  photoSize?: number;
};

type Area = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type CroppedAreaPixels = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const ImageCropDialog: FC<ImageCropDialogProps> = ({
  open,
  hide,
  imageSrc,
  onSave,
  title,
  saveButtonText,
  cancelButtonText,
  photoSize,
}) => {
  const localization = useLocalization() as any;

  // CMS'den gelen değerleri kullan, yoksa default değerleri kullan
  const cmsPhotoSize = photoSize || Number(localization?.web_pc_istanbulcard_apply_page_photo_size) || 600;
  const finalTitle = title || 'Fotoğrafı Düzenle';
  const finalSaveButtonText = saveButtonText || localization?.web_pc_istanbulcard_apply_page_photo_ok_text || 'Tamam';
  const finalCancelButtonText =
    cancelButtonText || localization?.web_pc_istanbulcard_apply_page_photo_cancel_text || 'İptal';

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: CroppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CroppedAreaPixels,
    rotation = 0
  ): Promise<string | null> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    // Rotation varsa, önce görüntüyü döndür
    const rotRad = (rotation * Math.PI) / 180;

    // Döndürülmüş görüntünün boyutlarını hesapla
    const rotatedWidth = Math.abs(Math.cos(rotRad) * image.width) + Math.abs(Math.sin(rotRad) * image.height);
    const rotatedHeight = Math.abs(Math.sin(rotRad) * image.width) + Math.abs(Math.cos(rotRad) * image.height);

    // Geçici canvas oluştur
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = rotatedWidth;
    tempCanvas.height = rotatedHeight;
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) {
      return null;
    }

    // Görüntüyü döndür
    tempCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
    tempCtx.rotate(rotRad);
    tempCtx.drawImage(image, -image.width / 2, -image.height / 2);

    // Crop canvas'ını oluştur
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Crop edilen alanı kopyala
    ctx.drawImage(
      tempCanvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Hedef boyuta scale et - KARE çıktı için (600x600)
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = cmsPhotoSize;
    finalCanvas.height = cmsPhotoSize;
    const finalCtx = finalCanvas.getContext('2d');

    if (finalCtx) {
      // Anti-aliasing için daha iyi kalite
      finalCtx.imageSmoothingEnabled = true;
      finalCtx.imageSmoothingQuality = 'high';

      // --- KARE KESİM İŞLEMİ ---

      // 1. Canvas'ı beyaz arka plan ile doldur (JPEG formatı için)
      finalCtx.fillStyle = '#FFFFFF';
      finalCtx.fillRect(0, 0, cmsPhotoSize, cmsPhotoSize);

      // 2. Crop edilen görüntüyü kare olarak çiz
      finalCtx.drawImage(canvas, 0, 0, pixelCrop.width, pixelCrop.height, 0, 0, cmsPhotoSize, cmsPhotoSize);

      // --- KARE KESİM İŞLEMİ BİTTİ ---
    }

    // Çıktıyı JPEG olarak al (kenarlar beyaz olur).
    // Şeffaf kenarlar istenirse 'image/png' kullanılmalı ve yukarıdaki fillStyle adımı kaldırılmalıdır.
    return finalCanvas.toDataURL('image/jpeg', 0.95);
  };

  const handleSave = useCallback(async () => {
    try {
      if (croppedAreaPixels) {
        const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
        if (croppedImage) {
          onSave(croppedImage, 'image/jpeg');
          handleClose();
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, rotation, imageSrc, onSave, cmsPhotoSize]);

  const handleClose = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    hide();
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 1));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const isZoomOutDisabled = zoom <= 1;
  const isZoomInDisabled = zoom >= 3;

  return (
    <Dialog open={open} onClose={handleClose} id="imageCropDialog" keepMounted={false} disablePortal={true}>
      <DialogCloseComponent closeFunction={handleClose} />
      <div className="imageCropDialog">
        <Box textAlign="center" mb={1.5} className="imageCropDialog__header">
          <Typography variant="h6" component="h1">
            <Box fontWeight={600}>{finalTitle}</Box>
          </Typography>
        </Box>

        <div className="imageCropDialog__content">
          <div className="imageCropDialog__cropper-container">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition={true}
              minZoom={1}
              objectFit="horizontal-cover"
              cropSize={{ width: 450, height: 450 }}
              maxZoom={3}
              zoomWithScroll={false}
              style={{
                containerStyle: {
                  backgroundColor: '#666',
                  width: '100%',
                  height: '100%',
                },
              }}
            />
          </div>

          <Box mt={1.5} mb={1}>
            <Typography variant="body2">
              <Box fontSize={fontSizes.smaller} mb={0.5}>
                Yakınlaştırma
              </Box>
            </Typography>
            <div className="imageCropDialog__zoom-controls">
              <IconButton
                size="small"
                aria-label="Uzaklaştır"
                onClick={isZoomOutDisabled ? undefined : handleZoomOut}
                className={`zoom-btn ${isZoomOutDisabled ? 'zoom-btn--disabled' : ''}`}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Slider
                value={zoom}
                onChange={(_, value) => setZoom(value as number)}
                min={1}
                max={3}
                step={0.01}
                className="imageCropDialog__slider"
              />
              <IconButton
                size="small"
                aria-label="Yakınlaştır"
                onClick={isZoomInDisabled ? undefined : handleZoomIn}
                className={`zoom-btn ${isZoomInDisabled ? 'zoom-btn--disabled' : ''}`}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </div>
          </Box>

          <Box mb={1.5}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleRotate}
              startIcon={<RotateRightIcon />}
            >
              Döndür (90°)
            </Button>
          </Box>
        </div>

        <div className="imageCropDialog__footer">
          <Button variant="contained" color="secondary" fullWidth onClick={handleSave} itemID="saveCroppedImageBtn">
            {finalSaveButtonText}
          </Button>

          <Box mt={1}>
            <Button variant="outlined" color="default" fullWidth onClick={handleClose} itemID="cancelCropBtn">
              {finalCancelButtonText}
            </Button>
          </Box>
        </div>
      </div>
    </Dialog>
  );
};

export default ImageCropDialog;